import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";

/**
 * Click-to-edit text for the proposal document.
 *
 * VIEW: renders `children` inside a <div> with `className` — exactly the
 * element the document printed before this existed, plus a screen-only
 * `du-inlineEdit` hook class and keyboard/click wiring. No wrapper element,
 * so the printed layout is unchanged.
 *
 * EDIT: swaps that element for a <textarea> that grows with its content and
 * inherits the same typography. A textarea is deliberate: it can only ever
 * hold plain text, so pasting from Word or a browser cannot carry formatting
 * into the proposal (contenteditable would).
 *
 * SAVING: `onChange` fires on every keystroke — the same path the old
 * standalone textareas used — so nothing waits for blur and a closed app
 * cannot lose the last edit.
 *
 * PRINT: if a print starts mid-edit, the editor is closed synchronously on
 * beforeprint so a textarea never reaches the page.
 */
type Props = {
  value: string;
  onChange: (next: string) => void;
  /** false → plain view element, identical to the non-editable markup. */
  editable: boolean;
  /** Class(es) of the printed element, e.g. "du-body". */
  className: string;
  /** Class(es) for the textarea; defaults to `className`. */
  editorClassName?: string;
  style?: React.CSSProperties;
  /** What the field is, for aria-labels ("The project"). */
  label: string;
  placeholder?: string;
  /** View element tag — "ul" for list-style blocks, which print as <ul>;
   *  "h2" / "p" / "span" for headings, paragraphs and inline words, so the
   *  view is still exactly the element the document printed. */
  as?: "div" | "ul" | "h2" | "p" | "span";
  /** A label or heading: Enter finishes instead of adding a line. (Callers
   *  still strip pasted line breaks in onChange.) */
  singleLine?: boolean;
  /** Mobile keyboard hint, e.g. "numeric" for a number of days. */
  inputMode?: "text" | "numeric";
  /** The editor opened (click, keyboard or autoOpen). */
  onOpen?: () => void;
  /** Open straight into edit mode on mount (used by "+ Add notes"). */
  autoOpen?: boolean;
  /** Select the whole text on open instead of placing the caret at the end
   *  (a new section's placeholder heading, meant to be typed over). */
  selectOnOpen?: boolean;
  onClose?: () => void;
  children: React.ReactNode;
};

export default function InlineEditText({
  value,
  onChange,
  editable,
  className,
  editorClassName,
  style,
  label,
  placeholder,
  as: Tag = "div",
  autoOpen = false,
  selectOnOpen = false,
  singleLine = false,
  inputMode,
  onOpen,
  onClose,
  children,
}: Props) {
  const [editing, setEditing] = useState<boolean>(autoOpen && editable);
  const taRef = useRef<HTMLTextAreaElement | null>(null);

  const close = useCallback(() => {
    setEditing(false);
    onClose?.();
  }, [onClose]);

  // Locked (e.g. while the startup draft loads) or made read-only mid-edit →
  // back to the view.
  useEffect(() => {
    if (!editable && editing) close();
  }, [editable, editing, close]);

  // Grow to fit the text: no inner scrollbar, no jump in height.
  useLayoutEffect(() => {
    const ta = taRef.current;
    if (!editing || !ta) return;
    ta.style.height = "auto";
    ta.style.height = `${ta.scrollHeight}px`;
  }, [editing, value]);

  // On open: focus with the caret at the end of the text.
  useEffect(() => {
    if (editing) onOpen?.();
    const ta = taRef.current;
    if (!editing || !ta) return;
    ta.focus();
    if (selectOnOpen) {
      ta.select();
    } else {
      const end = ta.value.length;
      ta.setSelectionRange(end, end);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  // Never print an open editor.
  useEffect(() => {
    if (!editing) return;
    const onBeforePrint = () => {
      flushSync(() => setEditing(false));
      onClose?.();
    };
    window.addEventListener("beforeprint", onBeforePrint);
    return () => window.removeEventListener("beforeprint", onBeforePrint);
  }, [editing, onClose]);

  if (editing) {
    return (
      <textarea
        ref={taRef}
        className={`${editorClassName ?? className} du-inlineEditor`}
        style={style}
        value={value}
        placeholder={placeholder}
        aria-label={label}
        rows={1}
        inputMode={inputMode}
        spellCheck={true}
        autoCorrect="on"
        autoCapitalize="sentences"
        onChange={(e) => onChange(e.target.value)}
        onBlur={close}
        onKeyDown={(e) => {
          if (e.key === "Escape" || (singleLine && e.key === "Enter")) {
            e.preventDefault();
            close();
          }
        }}
      />
    );
  }

  if (!editable) {
    return (
      <Tag className={className} style={style}>
        {children}
      </Tag>
    );
  }

  return (
    <Tag
      className={`${className} du-inlineEdit`}
      style={style}
      role="button"
      tabIndex={0}
      aria-label={`Edit ${label}`}
      title="Click to edit"
      data-placeholder={placeholder || "Click to add text"}
      onClick={() => setEditing(true)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setEditing(true);
        }
      }}
    >
      {children}
    </Tag>
  );
}
